import React, {useState, useEffect} from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import HomeIcon from '@mui/icons-material/Home';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CarIcon from '@mui/icons-material/DirectionsCar';
import ExitIcon from '@mui/icons-material/ExitToApp';
import OfferIcon from '@mui/icons-material/LocalOffer';
import NotifyIcon from '@mui/icons-material/NotificationsActive';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MoneyIcon from '@mui/icons-material/Money';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import {calcEst, showEst, optionsRequired, MAIN_COLOR} from '../common/sharedFunctions';
import SosIcon from '@mui/icons-material/Sos';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import { theme } from '../styles/theme';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SettingsIcon from '@mui/icons-material/Settings';
import { useThemeMode } from './Theme/ThemeModeContext';



const drawerWidth = 260;

export default function ResponsiveDrawer(props) {
  const { t, i18n  } = useTranslation();
  const isRTL = i18n.dir();
  const { window } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);
  const {
    signOff
  } = api;
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const dispatch = useDispatch();
  const { themeMode, setThemeMode } = useThemeMode();

  const LogOut = () => {
    dispatch(signOff());
  };

  const [role, setRole] = useState(null);

  useEffect(() => {
    if(auth.profile && auth.profile.usertype){
      setRole(auth.profile.usertype);
    }
  }, [auth.profile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleDrawer = () => {
    setMobileOpen(false);
  };
  const handleCollapseToggle = () => {
    setCollapsed(!collapsed);
  };
  const handleThemeMenuOpen = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };
  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  // Compute theme values based on themeMode
  const computedTheme = {
    ...theme,
    background:
      themeMode === 'dark' ? '#111' : themeMode === 'system' ? MAIN_COLOR : theme.white,
    text:
      themeMode === 'dark' ? '#fff' : themeMode === 'system' ? '#fff' : '#222',
    appBarBg:
      themeMode === 'dark' ? '#111' : themeMode === 'system' ? MAIN_COLOR : theme.white,
    appBarText:
      themeMode === 'dark' ? '#fff' : themeMode === 'system' ? '#fff' : theme.grayText,
    icon:
      themeMode === 'dark' ? '#fff' : themeMode === 'system' ? '#fff' : theme.grayText,
    divider:
      themeMode === 'dark' ? '#fff2' : themeMode === 'system' ? '#fff2' : theme.grayLight + '22',
  };

  const drawer = (
    <div style={{
      background: computedTheme.background, // Use computed background
      minHeight: '100vh',
      height: '100vh',
      transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1), background 0.3s cubic-bezier(0.4,0,0.2,1)',
      willChange: 'width',
      width: collapsed ? 72 : drawerWidth,
      boxShadow: theme.boxShadow,
      borderTopRightRadius: theme.borderRadius,
      borderBottomRightRadius: theme.borderRadius,
      position: 'relative',
      zIndex: 1200,
      overflow: 'hidden'
    }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'auto', marginTop: 24, marginBottom: 16 }}>
        <img style={{ margin: 0, width: collapsed ? 48 : 120, height: collapsed ? 48 : 120, transition: 'width 0.3s, height 0.3s', borderRadius: 16 }} src={require("../assets/img/logo192x192_transparent.png")} alt="Logo" />
      </div>
      <IconButton onClick={handleCollapseToggle} style={{ color: computedTheme.icon, margin: '0 auto', display: 'block', marginBottom: 8 }}>
        {isRTL === 'rtl'
          ? (collapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />)
          : (collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />)
        }
      </IconButton>
      {/* Scrollable nav list container */}
      <div
        className="hide-scrollbar"
        style={{
          transition: 'opacity 0.3s cubic-bezier(0.4,0,0.2,1)',
          opacity: collapsed ? 0.7 : 1,
          overflowY: 'auto',
          maxHeight: 'calc(100vh - 200px)',
          minHeight: 0,
          paddingBottom: 16,
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none' // IE/Edge
        }}
      >
        {role ?
        <List disablePadding dense style={{ transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)' }}>
          {[
            {name : t('home'), url:'/', icon: <HomeIcon/>, access: ['admin','fleetadmin','driver','customer']},
            {name : t('dashboard_text'), url:'/dashboard', icon: <DashboardIcon/>, access: ['admin','fleetadmin']},
            {name : t('dashboard_text'), url:'/admin_dashboard', icon: <DashboardIcon/>, access: ['admin_national','admin_regional','admin_provincial','admin_munincipality']},
            {name : t('user'), url:'/admin_creation/0', icon: <EmojiPeopleIcon/>, access: ['admin_national','admin_regional','admin_provincial','admin_munincipality']},
            {name : t('booking_history'), url:'/bookings', icon: <ViewListIcon/>, access: ['admin','fleetadmin','driver','customer','admin_munincipality']},
            {name : t('addbookinglable'), url:'/addbookings', icon: <ContactPhoneIcon/>, access: calcEst && !showEst && !optionsRequired ? ['customer'] : ['admin','fleetadmin','customer']},

            {name : t('user'), url:'/users/0', icon: <EmojiPeopleIcon />, access: ['admin','fleetadmin']},

            {name : t('car_type'), url:'/cartypes', icon: <CarIcon/>, access: ['admin']},
            {name : t('cars'), url:'/cars', icon: <CarIcon/>, access: ['admin','fleetadmin','driver']},
            
            {name : t('withdraws_web'), url:'/withdraws', icon: <MoneyIcon />, access: ['admin']},
            {name : t('add_to_wallet'), url:'/addtowallet', icon: <AccountBalanceWalletIcon />, access: ['admin']},
          
            {name : t('report'), url:'/allreports', icon: <AssessmentIcon />, access: ['admin','fleetadmin']},
            {name : t('promo'), url:'/promos', icon: <OfferIcon />, access: ['admin']},
            {name : t('push_notifications'), url:'/notifications', icon: <NotifyIcon />, access: ['admin']},
            {name : t('sos'), url:'/sos', icon: <SosIcon />, access: ['admin']},
            {name : t('complain'), url:'/complain', icon: <AnnouncementOutlinedIcon/>, access: ['admin']},
            {name : t('my_wallet_tile'), url:'/userwallet', icon: <AccountBalanceWalletIcon />, access: ['driver','customer','fleetadmin']},
            {name : t('settings_title'), url:'/settings', icon: <PhoneIphoneIcon />, access: ['admin']},
            {name : t('payment_settings'), url:'/paymentsettings', icon: <PhoneIphoneIcon />, access: ['admin']},
            {name : t('profile'), url:'/profile', icon: <AccountCircleIcon />, access: ['admin','fleetadmin','driver','customer']},
            {name : t('logout'), url:'logout', icon: <ExitIcon />, access: ['admin','fleetadmin','driver','customer','admin_national','admin_regional','admin_provincial','admin_munincipality']},
          ].map((item, index) => (
            item.access.includes(role)?
              <div style={{ display: 'flex', height: '50px', transition: 'background 0.3s cubic-bezier(0.4,0,0.2,1)' }} key={"key" + index}>
                <ListItem key={item} disableGutters disablePadding style={{ paddingLeft: 5 }} alignItems='center' dense onClick={handleDrawer}>
                  <ListItemButton
                    disableGutters
                    dense
                    component={Link}
                    to={item.url==='logout'? null : item.url}
                    onClick={item.url==='logout'? LogOut : null}
                    style={{
                      borderRadius: theme.borderRadius,
                      margin: 4,
                      background: 'transparent',
                      transition: 'background 0.2s',
                      color: computedTheme.text,
                      fontFamily: theme.fontFamily, // Use modern font
                    }}
                    onMouseOver={e => e.currentTarget.style.background = computedTheme.divider}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <ListItemIcon style={{ color: computedTheme.icon, minWidth: collapsed ? 40 : 56, justifyContent: 'center', transition: 'min-width 0.3s cubic-bezier(0.4,0,0.2,1)', fontFamily: theme.fontFamily }}>
                      {item.icon}
                    </ListItemIcon>
                    <div style={{
                      width: collapsed ? 0 : 'auto',
                      opacity: collapsed ? 0 : 1,
                      transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s cubic-bezier(0.4,0,0.2,1)',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap'
                    }}>
                      {!collapsed && <ListItemText inset={false} disableTypography primary={<Typography style={{ color: computedTheme.text, textAlign: isRTL === 'rtl' ? 'right' : 'left', fontFamily: theme.fontFamily }}>{item.name}</Typography>} />}
                    </div>
                  </ListItemButton>
                  <Divider style={{ background: computedTheme.divider }} />
                </ListItem>
              </div>
            :null
          ))}
        </List>
        : null }
      </div>
    </div>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex', direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        style={{
          background: computedTheme.appBarBg,
          boxShadow: theme.boxShadow,
          borderBottomLeftRadius: theme.borderRadius,
          borderBottomRightRadius: theme.borderRadius,
          transition: 'margin 0.3s cubic-bezier(0.4,0,0.2,1)',
          marginLeft: collapsed ? 72 : drawerWidth
        }}
        sx={{
          width: { sm: `calc(100% - ${collapsed ? 72 : drawerWidth}px)` },
          ml: { sm: `${collapsed ? 72 : drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            {isRTL === 'rtl'
              ? (collapsed ? <ChevronLeftIcon style={{ color: computedTheme.appBarText }} /> : <ChevronRightIcon style={{ color: computedTheme.appBarText }} />)
              : (collapsed ? <ChevronRightIcon style={{ color: computedTheme.appBarText }} /> : <ChevronLeftIcon style={{ color: computedTheme.appBarText }} />)
            }
          </IconButton>
          <Typography variant="h6" noWrap component="div" style={{ color: computedTheme.appBarText, fontFamily: theme.fontFamily }}>
            {settings && settings.appName ? settings.appName : ''}
          </Typography>
          {/* Gear icon for theme options */}
          <div style={{ flex: 1 }} />
          <IconButton
            color="inherit"
            aria-label="theme settings"
            onClick={handleThemeMenuOpen}
            sx={{ ml: 2 }}
          >
            <SettingsIcon style={{ color: computedTheme.icon, fontSize: 28 }} />
          </IconButton>
          <Menu
            anchorEl={themeMenuAnchor}
            open={Boolean(themeMenuAnchor)}
            onClose={handleThemeMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem
              selected={themeMode === 'light'}
              onClick={() => { setThemeMode('light'); handleThemeMenuClose(); }}
            >Light Mode</MenuItem>
            <MenuItem
              selected={themeMode === 'dark'}
              onClick={() => { setThemeMode('dark'); handleThemeMenuClose(); }}
            >Dark Mode</MenuItem>
            <MenuItem
              selected={themeMode === 'system'}
              onClick={() => { setThemeMode('system'); handleThemeMenuClose(); }}
            >System Default</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: collapsed ? 72 : drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed ? 72 : drawerWidth,
              transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
              willChange: 'width',
              direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
              overflowX: 'hidden'
            }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          anchor={isRTL === 'rtl' ? 'right' : 'left'}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed ? 72 : drawerWidth,
              transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
              willChange: 'width',
              direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
              overflowX: 'hidden'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, width: { sm: `calc(100% - ${collapsed ? 72 : drawerWidth}px)` } }}
      >
        <Toolbar />
        {props.children}
      </Box>
    </Box>
  );
}